/**
 * My Schedule Page — Unified Calendar View
 *
 * Combines all schedule types for the current user:
 * - Tasks assigned to me
 * - My shifts (workforce)
 * - My training schedules
 *
 * Uses FullCalendar with color-coded event types.
 * Dumb frontend pattern (DRR-055).
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Group,
  Stack,
  Text,
  Title,
  Alert,
  Loader,
  ThemeIcon,
  SegmentedControl,
  Paper,
  Chip,
  Box,
} from '@mantine/core';
import {
  IconCalendarUser,
  IconAlertTriangle,
  IconChecklist,
  IconClock,
  IconSchool,
} from '@tabler/icons-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import type { EventClickArg, EventInput } from '@fullcalendar/core';
import { useNavigate } from 'react-router-dom';
import {
  useListTasksQuery,
  GqlTaskStatus,
  type TaskFieldsFragment,
} from '@/generated/graphql';
import { useDevActorId } from '@/hooks/useDevActorId';

type EventType = 'task' | 'shift' | 'training';
type ViewMode = 'week' | 'day' | 'month' | 'list';

const EVENT_TYPE_COLORS: Record<EventType, { bg: string; border: string }> = {
  task: { bg: '#228be6', border: '#1c7ed6' },
  shift: { bg: '#40c057', border: '#37b24d' },
  training: { bg: '#be4bdb', border: '#9c36b5' },
};

const DEFAULT_DURATION_MS = 3_600_000;

export function MySchedulePage() {
  const navigate = useNavigate();
  const { actorId } = useDevActorId();
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [enabledTypes, setEnabledTypes] = useState<EventType[]>(['task', 'shift', 'training']);

  // Fetch my tasks
  const { data: taskData, loading: taskLoading, error: taskError } = useListTasksQuery({
    variables: { limit: 200 },
  });

  // Filter to my tasks only
  const myTasks = useMemo(() => {
    const items = (taskData?.taskApiListTasks?.items ?? []) as TaskFieldsFragment[];
    return items.filter(
      (task) =>
        task.assignedActor?.id === actorId &&
        task.status !== GqlTaskStatus.COMPLETED &&
        task.status !== GqlTaskStatus.CANCELLED
    );
  }, [taskData, actorId]);

  // Convert tasks to calendar events
  const taskEvents: EventInput[] = useMemo(() => {
    if (!enabledTypes.includes('task')) return [];
    return myTasks.map((task) => {
      const start = task.scheduledStart || task.startedAt || task.createdAt;
      const end =
        task.scheduledEnd ||
        task.completedAt ||
        new Date(new Date(start).getTime() + DEFAULT_DURATION_MS).toISOString();
      return {
        id: `task-${task.id}`,
        title: task.name,
        start,
        end,
        backgroundColor: EVENT_TYPE_COLORS.task.bg,
        borderColor: EVENT_TYPE_COLORS.task.border,
        extendedProps: {
          type: 'task' as EventType,
          entityId: task.id,
          status: task.status,
        },
      };
    });
  }, [myTasks, enabledTypes]);

  // TODO: Add shift events from useMyShiftsQuery when available
  const shiftEvents: EventInput[] = useMemo(() => {
    if (!enabledTypes.includes('shift')) return [];
    // Placeholder - wire to actual shift query
    return [];
  }, [enabledTypes]);

  // TODO: Add training events from useMyTrainingSchedulesQuery when available
  const trainingEvents: EventInput[] = useMemo(() => {
    if (!enabledTypes.includes('training')) return [];
    // Placeholder - wire to actual training query
    return [];
  }, [enabledTypes]);

  // Combine all events
  const allEvents = useMemo(
    () => [...taskEvents, ...shiftEvents, ...trainingEvents],
    [taskEvents, shiftEvents, trainingEvents]
  );

  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      const { type, entityId } = info.event.extendedProps;
      if (type === 'task') {
        navigate(`/ferp-core/tasks/${entityId}`);
      } else if (type === 'shift') {
        navigate(`/workforce/shifts/${entityId}`);
      } else if (type === 'training') {
        navigate(`/training/${entityId}`);
      }
    },
    [navigate]
  );

  const toggleEventType = (type: EventType) => {
    setEnabledTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const viewMap: Record<ViewMode, string> = {
    week: 'timeGridWeek',
    day: 'timeGridDay',
    month: 'dayGridMonth',
    list: 'listWeek',
  };

  if (taskError) {
    return (
      <Container fluid py="md" px="md">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error">
          {taskError.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid py="md" px="md">
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            <ThemeIcon variant="light" size="lg" color="violet">
              <IconCalendarUser size={20} />
            </ThemeIcon>
            <div>
              <Title order={3}>My Schedule</Title>
              <Text size="xs" c="dimmed">
                Tasks, shifts, and training in one view
              </Text>
            </div>
          </Group>

          <Group gap="sm">
            <SegmentedControl
              size="xs"
              value={viewMode}
              onChange={(v) => setViewMode(v as ViewMode)}
              data={[
                { label: 'Week', value: 'week' },
                { label: 'Day', value: 'day' },
                { label: 'Month', value: 'month' },
                { label: 'List', value: 'list' },
              ]}
            />
          </Group>
        </Group>

        {/* Event type filters */}
        <Group gap="xs">
          <Text size="xs" c="dimmed" fw={500}>
            Show:
          </Text>
          <Chip
            checked={enabledTypes.includes('task')}
            onChange={() => toggleEventType('task')}
            size="xs"
            color="blue"
            variant="filled"
          >
            <Group gap={4}>
              <IconChecklist size={12} />
              Tasks ({myTasks.length})
            </Group>
          </Chip>
          <Chip
            checked={enabledTypes.includes('shift')}
            onChange={() => toggleEventType('shift')}
            size="xs"
            color="green"
            variant="filled"
          >
            <Group gap={4}>
              <IconClock size={12} />
              Shifts
            </Group>
          </Chip>
          <Chip
            checked={enabledTypes.includes('training')}
            onChange={() => toggleEventType('training')}
            size="xs"
            color="grape"
            variant="filled"
          >
            <Group gap={4}>
              <IconSchool size={12} />
              Training
            </Group>
          </Chip>
        </Group>

        {/* Calendar */}
        {taskLoading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <Paper shadow="xs" radius="sm" withBorder p="md">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView={viewMap[viewMode]}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: '',
              }}
              events={allEvents}
              eventClick={handleEventClick}
              editable={false}
              selectable={false}
              dayMaxEvents={4}
              weekends={true}
              height="auto"
              nowIndicator={true}
              eventDisplay="block"
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5],
                startTime: '08:00',
                endTime: '18:00',
              }}
              slotDuration="00:30:00"
            />
          </Paper>
        )}

        {/* Legend */}
        <Group gap="md">
          <Text size="xs" c="dimmed" fw={500}>
            Legend:
          </Text>
          {(['task', 'shift', 'training'] as EventType[]).map((type) => (
            <Group key={type} gap={4}>
              <Box
                w={10}
                h={10}
                style={{
                  backgroundColor: EVENT_TYPE_COLORS[type].bg,
                  borderRadius: 2,
                }}
              />
              <Text size="xs" c="dimmed" tt="capitalize">
                {type}
              </Text>
            </Group>
          ))}
        </Group>
      </Stack>
    </Container>
  );
}
